import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Users, UserCheck, Euro, Plus, Pencil, Trash2, Calendar, AlertCircle, Shield } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from "date-fns";
import { de } from "date-fns/locale";


const getHoursBetween = (startTime, endTime) => {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let hours = (endH + endM / 60) - (startH + startM / 60);
  if (hours < 0) hours += 24; // handle overnight shifts
  return hours;
};

export default function Employees() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedPayrollMonth, setSelectedPayrollMonth] = useState(new Date());
  const [editingUser, setEditingUser] = useState(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    first_name: '', last_name: '', hourly_rate: '', is_active: true,
  });
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: 'Küche',
    shift: 'Flexibel',
    shift_start_lunch: '11:00',
    shift_end_lunch: '15:00',
    shift_start_dinner: '17:00',
    shift_end_dinner: '23:00',
    contact: '',
    hourly_rate: '',
    active: true
  });

  const queryClient = useQueryClient();

  const { data: employees } = useQuery({
    queryKey: ['employees'],
    queryFn: () => base44.entities.Employee.list(),
    initialData: [],
  });

  const { data: shifts } = useQuery({
    queryKey: ['shifts', format(currentMonth, 'yyyy-MM'), format(selectedPayrollMonth, 'yyyy-MM')],
    queryFn: () => base44.entities.Shift.list(),
    initialData: [],
  });

  const { data: sales } = useQuery({
    queryKey: ['sales'],
    queryFn: () => base44.entities.Sale.list('-date'),
    initialData: [],
  });

  const { data: restaurant } = useQuery({
    queryKey: ['restaurant'],
    queryFn: async () => {
      const data = await base44.entities.Restaurant.list();
      return data[0];
    },
  });

  const createEmployeeMutation = useMutation({
    mutationFn: (employeeData) => base44.entities.Employee.create(employeeData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setIsDialogOpen(false);
      setEditingEmployee(null);
      resetForm();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Employee.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setIsDialogOpen(false);
      setEditingEmployee(null);
      resetForm();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: (id) => base44.entities.Employee.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setDeleteEmployeeId(null);
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message || 'Delete failed');
      setDeleteEmployeeId(null);
    },
  });

  const createShiftMutation = useMutation({
    mutationFn: (shiftData) => base44.entities.Shift.create(shiftData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const deleteShiftMutation = useMutation({
    mutationFn: (id) => base44.entities.Shift.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message || 'Delete failed');
    },
  });

  const { data: orgUsers = [] } = useQuery({
    queryKey: ['org-users'],
    queryFn: () => base44.organizations.listUsers(),
    initialData: [],
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => base44.entities.Task.list('-due_date'),
    initialData: [],
    refetchInterval: 10000,
  });

  const employeeUsers = orgUsers.filter(u => u.role === 'staff');
  const today = format(new Date(), 'yyyy-MM-dd');

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => base44.organizations.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
      setIsUserDialogOpen(false);
      setEditingUser(null);
    },
    onError: (err) => alert(err?.response?.data?.detail || err.message),
  });

  const resetForm = () => {
    setNewEmployee({
      name: '',
      role: 'Küche',
      shift: 'Flexibel',
      shift_start_lunch: '11:00',
      shift_end_lunch: '15:00',
      shift_start_dinner: '17:00',
      shift_end_dinner: '23:00',
      contact: '',
      hourly_rate: '',
      active: true
    });
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setNewEmployee({
      name: employee.name || '',
      role: employee.role || 'Küche',
      shift: employee.shift || 'Flexibel',
      shift_start_lunch: employee.shift_start_lunch || '11:00',
      shift_end_lunch: employee.shift_end_lunch || '15:00',
      shift_start_dinner: employee.shift_start_dinner || '17:00',
      shift_end_dinner: employee.shift_end_dinner || '23:00',
      contact: employee.contact || '',
      hourly_rate: employee.hourly_rate?.toString() || '',
      active: employee.active !== false
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const employeeData = {
      ...newEmployee,
      hourly_rate: parseFloat(newEmployee.hourly_rate) || 0,
    };

    if (editingEmployee) {
      updateEmployeeMutation.mutate({ id: editingEmployee.id, data: employeeData });
    } else {
      createEmployeeMutation.mutate(employeeData);
    }
  };

  const handleAssignShift = (date, shiftType, employee) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check if shift already exists
    const existingShift = shifts.find(s => s.date === dateStr && s.shift_type === shiftType && s.employee_id === employee.id);
    
    if (existingShift) {
      // Remove shift
      deleteShiftMutation.mutate(existingShift.id);
    } else {
      // Add shift
      createShiftMutation.mutate({
        date: dateStr,
        shift_type: shiftType,
        employee_id: employee.id,
        employee_name: employee.name,
        role: employee.role
      });
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      hourly_rate: user.hourly_rate?.toString() || '',
      is_active: user.is_active !== false,
    });
    setIsUserDialogOpen(true);
  };

  const handleUserSubmit = (e) => {
    e.preventDefault();
    updateUserMutation.mutate({
      id: editingUser.id,
      data: { ...userForm, hourly_rate: parseFloat(userForm.hourly_rate) || 0 },
    });
  };

  // Calculate recommended staff based on forecast
  const getRecommendedStaff = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayOfWeek = date.getDay();
    const dayName = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'][dayOfWeek];
    
    // Check if restaurant is open
    if (restaurant && restaurant.opening_days && !restaurant.opening_days.includes(dayName)) {
      return { lunch: 0, dinner: 0, isOpen: false };
    }

    // Calculate average guests for this day of week
    const salesByDay = sales.filter(sale => {
      const saleDate = new Date(sale.date);
      return saleDate.getDay() === dayOfWeek;
    });

    const avgLunchGuests = salesByDay.filter(s => s.shift === 'Mittag').reduce((sum, s) => sum + (s.guests_count || 0), 0) / Math.max(salesByDay.filter(s => s.shift === 'Mittag').length, 1);
    const avgDinnerGuests = salesByDay.filter(s => s.shift === 'Abend').reduce((sum, s) => sum + (s.guests_count || 0), 0) / Math.max(salesByDay.filter(s => s.shift === 'Abend').length, 1);

    // Rule of thumb: 1 staff per 15-20 guests
    const lunchStaff = Math.max(2, Math.ceil(avgLunchGuests / 18));
    const dinnerStaff = Math.max(2, Math.ceil(avgDinnerGuests / 18));

    return {
      lunch: restaurant?.lunch_service !== false ? lunchStaff : 0,
      dinner: restaurant?.dinner_service !== false ? dinnerStaff : 0,
      isOpen: true,
      avgLunchGuests: Math.round(avgLunchGuests),
      avgDinnerGuests: Math.round(avgDinnerGuests)
    };
  };

  // Calculate payroll for selected month
  const calculatePayroll = () => {
    const monthStart = startOfMonth(selectedPayrollMonth);
    const monthEnd = endOfMonth(selectedPayrollMonth);
    const monthShifts = shifts.filter(s => {
      const shiftDate = new Date(s.date);
      return shiftDate >= monthStart && shiftDate <= monthEnd;
    });

    const payrollByEmployee = {};
    
    employees.forEach(emp => {
      const empShifts = monthShifts.filter(s => s.employee_id === emp.id);
      
      // Berechne tatsächliche Stunden basierend auf Schichtzeiten
      let totalHours = 0;
      empShifts.forEach(shift => {
        if (shift.shift_type === 'Mittag' && emp.shift_start_lunch && emp.shift_end_lunch) {
          totalHours += getHoursBetween(emp.shift_start_lunch, emp.shift_end_lunch);
        } else if (shift.shift_type === 'Abend' && emp.shift_start_dinner && emp.shift_end_dinner) {
          totalHours += getHoursBetween(emp.shift_start_dinner, emp.shift_end_dinner);
        } else {
          totalHours += 8;
        }
      });

      const totalShifts = empShifts.length;
      const totalPay = totalHours * (emp.hourly_rate || 0);
      
      const lunchShifts = empShifts.filter(s => s.shift_type === 'Mittag').length;
      const dinnerShifts = empShifts.filter(s => s.shift_type === 'Abend').length;
      
      payrollByEmployee[emp.id] = {
        employee: emp,
        totalShifts,
        lunchShifts,
        dinnerShifts,
        totalHours: Math.round(totalHours * 10) / 10, // Runde auf 1 Dezimalstelle
        hourlyRate: emp.hourly_rate || 0,
        totalPay,
        shifts: empShifts // Store actual shifts for detailed breakdown
      };
    });

    return payrollByEmployee;
  };

  const payrollData = calculatePayroll();
  const totalPayroll = Object.values(payrollData).reduce((sum, p) => sum + p.totalPay, 0);
  const totalHours = Object.values(payrollData).reduce((sum, p) => sum + p.totalHours, 0);

  const roleColors = {
    'Küche': 'bg-blue-100 text-blue-700 border-blue-200',
    'Pizzabäcker': 'bg-orange-100 text-orange-700 border-orange-200',
    'FOH': 'bg-purple-100 text-purple-700 border-purple-200',
    'Manager': 'bg-green-100 text-green-700 border-green-200',
    'Aushilfe': 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const roleLabels = {
    'Küche': t("employees.roles.kitchen"),
    'Pizzabäcker': t("employees.roles.pizzaBaker"),
    'FOH': t("employees.roles.foh"),
    'Manager': t("employees.roles.manager"),
    'Aushilfe': t("employees.roles.helper"),
  };

  const shiftColors = {
    'Mittag': 'bg-yellow-100 text-yellow-700',
    'Abend': 'bg-indigo-100 text-indigo-700',
    'Flexibel': 'bg-slate-100 text-slate-700',
  };

  const shiftLabels = {
    'Mittag': t("employees.shifts.lunch"),
    'Abend': t("employees.shifts.dinner"),
    'Flexibel': t("employees.shifts.flexible"),
  };

  const activeEmployees = employees.filter(e => e.active !== false);
  const avgHourlyRate = activeEmployees.length > 0
    ? activeEmployees.reduce((sum, e) => sum + (e.hourly_rate || 0), 0) / activeEmployees.length
    : 0;

  const employeesByRole = {
    'Küche': employees.filter(e => e.role === 'Küche'),
    'Pizzabäcker': employees.filter(e => e.role === 'Pizzabäcker'),
    'FOH': employees.filter(e => e.role === 'FOH'),
    'Manager': employees.filter(e => e.role === 'Manager'),
    'Aushilfe': employees.filter(e => e.role === 'Aushilfe'),
  };

  // Generate days for current month
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t("employees.title")}</h1>
            <p className="text-slate-500 mt-1">{t("employees.subtitle")}</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingEmployee(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 shadow-md">
                <Plus className="w-4 h-4 mr-2" />
                {t("employees.addEmployee")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingEmployee ? t("employees.editEmployee") : t("employees.newEmployee")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">{t("employees.name")}</Label>
                    <Input
                      id="name"
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                      placeholder={t("employees.namePlaceholder")}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">{t("employees.role")}</Label>
                    <Select value={newEmployee.role} onValueChange={(value) => setNewEmployee({...newEmployee, role: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Küche">{t("employees.roles.kitchen")}</SelectItem>
                        <SelectItem value="Pizzabäcker">{t("employees.roles.pizzaBaker")}</SelectItem>
                        <SelectItem value="FOH">{t("employees.roles.foh")}</SelectItem>
                        <SelectItem value="Manager">{t("employees.roles.manager")}</SelectItem>
                        <SelectItem value="Aushilfe">{t("employees.roles.helper")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="shift">{t("employees.preferredShift")}</Label>
                    <Select value={newEmployee.shift} onValueChange={(value) => setNewEmployee({...newEmployee, shift: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mittag">{t("employees.shifts.lunch")}</SelectItem>
                        <SelectItem value="Abend">{t("employees.shifts.dinner")}</SelectItem>
                        <SelectItem value="Flexibel">{t("employees.shifts.flexible")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="hourly_rate">{t("employees.hourlyRate")}</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      step="0.01"
                      value={newEmployee.hourly_rate}
                      onChange={(e) => setNewEmployee({...newEmployee, hourly_rate: e.target.value})}
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3">{t("employees.shiftTimes")}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-slate-700">{t("employees.lunchShift")}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="shift_start_lunch" className="text-xs text-slate-600">{t("employees.from")}</Label>
                          <Input
                            id="shift_start_lunch"
                            type="time"
                            value={newEmployee.shift_start_lunch}
                            onChange={(e) => setNewEmployee({...newEmployee, shift_start_lunch: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="shift_end_lunch" className="text-xs text-slate-600">{t("employees.to")}</Label>
                          <Input
                            id="shift_end_lunch"
                            type="time"
                            value={newEmployee.shift_end_lunch}
                            onChange={(e) => setNewEmployee({...newEmployee, shift_end_lunch: e.target.value})}
                          />
                        </div>
                      </div>
                      {newEmployee.shift_start_lunch && newEmployee.shift_end_lunch && (
                        <p className="text-xs text-slate-500 mt-2">
                          {t("employees.approxHours", { hours: getHoursBetween(newEmployee.shift_start_lunch, newEmployee.shift_end_lunch).toFixed(1) })}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-slate-700">{t("employees.dinnerShift")}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="shift_start_dinner" className="text-xs text-slate-600">{t("employees.from")}</Label>
                          <Input
                            id="shift_start_dinner"
                            type="time"
                            value={newEmployee.shift_start_dinner}
                            onChange={(e) => setNewEmployee({...newEmployee, shift_start_dinner: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="shift_end_dinner" className="text-xs text-slate-600">{t("employees.to")}</Label>
                          <Input
                            id="shift_end_dinner"
                            type="time"
                            value={newEmployee.shift_end_dinner}
                            onChange={(e) => setNewEmployee({...newEmployee, shift_end_dinner: e.target.value})}
                          />
                        </div>
                      </div>
                      {newEmployee.shift_start_dinner && newEmployee.shift_end_dinner && (
                        <p className="text-xs text-slate-500 mt-2">
                          {t("employees.approxHours", { hours: getHoursBetween(newEmployee.shift_start_dinner, newEmployee.shift_end_dinner).toFixed(1) })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact">{t("employees.phone")}</Label>
                  <Input
                    id="contact"
                    value={newEmployee.contact}
                    onChange={(e) => setNewEmployee({...newEmployee, contact: e.target.value})}
                    placeholder={t("employees.phonePlaceholder")}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="active"
                    checked={newEmployee.active}
                    onCheckedChange={(checked) => setNewEmployee({...newEmployee, active: checked})}
                  />
                  <Label htmlFor="active" className="text-sm cursor-pointer">
                    {t("employees.activeEmployee")}
                  </Label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t("employees.cancel")}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editingEmployee ? t("employees.update") : t("employees.save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteEmployeeId} onOpenChange={() => setDeleteEmployeeId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("employees.deleteEmployee")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("employees.deleteEmployeeDesc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("employees.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteEmployeeMutation.mutate(deleteEmployeeId)}
                className="bg-red-600 hover:bg-red-700"
              >
                {t("employees.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="overview">
              <Users className="w-4 h-4 mr-2" />
              {t("employees.overview")}
            </TabsTrigger>
            <TabsTrigger value="planner">
              <Calendar className="w-4 h-4 mr-2" />
              {t("employees.shiftPlanner")}
            </TabsTrigger>
            <TabsTrigger value="payroll">
              <Euro className="w-4 h-4 mr-2" />
              {t("employees.payroll")}
            </TabsTrigger>
            <TabsTrigger value="user-accounts">
              <Shield className="w-4 h-4 mr-2" />
              User Accounts
              {employeeUsers.length > 0 && (
                <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5">{employeeUsers.length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("employees.totalEmployees")}</p>
                      <p className="text-2xl font-bold text-slate-900">{employees.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-xl">
                      <UserCheck className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("employees.active")}</p>
                      <p className="text-2xl font-bold text-slate-900">{activeEmployees.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-xl">
                      <Euro className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("employees.avgHourlyRate")}</p>
                      <p className="text-2xl font-bold text-slate-900">€{avgHourlyRate.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Employees by Role */}
            <div className="space-y-6">
              {Object.keys(employeesByRole).map(role => (
                employeesByRole[role].length > 0 && (
                  <Card key={role} className="border-0 shadow-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Badge className={`${roleColors[role]} border`}>
                          {roleLabels[role] || role}
                        </Badge>
                        <span className="text-slate-500 text-sm font-normal">
                          ({t("employees.employeesCount", { count: employeesByRole[role].length })})
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {employeesByRole[role].map(employee => (
                          <div 
                            key={employee.id} 
                            className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors relative"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="font-semibold text-slate-900">{employee.name}</h3>
                                <p className="text-sm text-slate-500 mt-1">{roleLabels[employee.role] || employee.role}</p>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleEdit(employee)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setDeleteEmployeeId(employee.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              {employee.shift && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600">{t("employees.preferred")}</span>
                                  <Badge className={shiftColors[employee.shift]}>
                                    {shiftLabels[employee.shift] || employee.shift}
                                  </Badge>
                                </div>
                              )}

                              {employee.shift_start_lunch && employee.shift_end_lunch && (
                                <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2">
                                  <span className="text-yellow-700 font-medium">{t("employees.lunchShift")}: </span>
                                  <span className="text-yellow-900">{employee.shift_start_lunch} - {employee.shift_end_lunch}</span>
                                </div>
                              )}

                              {employee.shift_start_dinner && employee.shift_end_dinner && (
                                <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2">
                                  <span className="text-indigo-700 font-medium">{t("employees.dinnerShift")}: </span>
                                  <span className="text-indigo-900">{employee.shift_start_dinner} - {employee.shift_end_dinner}</span>
                                </div>
                              )}

                              {employee.hourly_rate && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600">{t("employees.hourlyRateLabel")}</span>
                                  <span className="font-semibold">€{employee.hourly_rate.toFixed(2)}</span>
                                </div>
                              )}

                              {employee.contact && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600">{t("employees.phone")}:</span>
                                  <span className="font-medium">{employee.contact}</span>
                                </div>
                              )}

                              {employee.active === false && (
                                <Badge variant="outline" className="text-xs mt-2">{t("employees.inactive")}</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )
              ))}

              {employees.length === 0 && (
                <Card className="border-0 shadow-md">
                  <CardContent className="text-center py-12">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{t("employees.noEmployees")}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="planner">
            {/* Month Navigation */}
            <Card className="border-0 shadow-md mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <Button variant="outline" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                    {t("employees.previousMonth")}
                  </Button>
                  <h2 className="text-xl font-bold text-slate-900">
                    {format(currentMonth, 'MMMM yyyy', { locale: de })}
                  </h2>
                  <Button variant="outline" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                    {t("employees.nextMonth")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Shift Planner */}
            <div className="space-y-3">
              {daysInMonth.map(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayShifts = shifts.filter(s => s.date === dateStr);
                const lunchShifts = dayShifts.filter(s => s.shift_type === 'Mittag');
                const dinnerShifts = dayShifts.filter(s => s.shift_type === 'Abend');
                const recommended = getRecommendedStaff(day);
                const dayName = format(day, 'EEEE', { locale: de });
                
                if (!recommended.isOpen) {
                  return (
                    <Card key={dateStr} className="border-0 shadow-sm bg-slate-100">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="text-center min-w-[80px]">
                            <p className="text-sm font-medium text-slate-400">{dayName}</p>
                            <p className="text-lg font-bold text-slate-400">{format(day, 'dd.MM')}</p>
                          </div>
                          <div className="text-sm text-slate-400">{t("employees.closed")}</div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }

                const lunchUnderstaffed = lunchShifts.length < recommended.lunch;
                const dinnerUnderstaffed = dinnerShifts.length < recommended.dinner;

                return (
                  <Card key={dateStr} className="border-0 shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="text-center min-w-[80px]">
                          <p className="text-sm font-medium text-slate-500">{dayName}</p>
                          <p className="text-2xl font-bold text-slate-900">{format(day, 'dd.MM')}</p>
                        </div>

                        <div className="flex-1 space-y-4">
                          {/* Mittag */}
                          {recommended.lunch > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-yellow-100 text-yellow-700">{t("employees.lunch")}</Badge>
                                  <span className="text-sm text-slate-500">
                                    {lunchShifts.length}/{recommended.lunch} {t("employees.employeesAssigned")}
                                  </span>
                                  {lunchUnderstaffed && (
                                    <AlertCircle className="w-4 h-4 text-orange-500" />
                                  )}
                                </div>
                                <span className="text-xs text-slate-400">
                                  ~{recommended.avgLunchGuests} {t("employees.guestsExpected")}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {activeEmployees.map(emp => {
                                  const isAssigned = lunchShifts.some(s => s.employee_id === emp.id);
                                  return (
                                    <Button
                                      key={emp.id}
                                      variant={isAssigned ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => handleAssignShift(day, 'Mittag', emp)}
                                      className={isAssigned ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                                    >
                                      {emp.name}
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Abend */}
                          {recommended.dinner > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-indigo-100 text-indigo-700">{t("employees.dinner")}</Badge>
                                  <span className="text-sm text-slate-500">
                                    {dinnerShifts.length}/{recommended.dinner} {t("employees.employeesAssigned")}
                                  </span>
                                  {dinnerUnderstaffed && (
                                    <AlertCircle className="w-4 h-4 text-orange-500" />
                                  )}
                                </div>
                                <span className="text-xs text-slate-400">
                                  ~{recommended.avgDinnerGuests} {t("employees.guestsExpected")}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {activeEmployees.map(emp => {
                                  const isAssigned = dinnerShifts.some(s => s.employee_id === emp.id);
                                  return (
                                    <Button
                                      key={emp.id}
                                      variant={isAssigned ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => handleAssignShift(day, 'Abend', emp)}
                                      className={isAssigned ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                                    >
                                      {emp.name}
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="payroll">
            {/* Month Selection */}
            <Card className="border-0 shadow-md mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <Button variant="outline" onClick={() => setSelectedPayrollMonth(subMonths(selectedPayrollMonth, 1))}>
                    {t("employees.previousMonth")}
                  </Button>
                  <h2 className="text-xl font-bold text-slate-900">
                    {format(selectedPayrollMonth, 'MMMM yyyy', { locale: de })}
                  </h2>
                  <Button variant="outline" onClick={() => setSelectedPayrollMonth(addMonths(selectedPayrollMonth, 1))}>
                    {t("employees.nextMonth")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("employees.totalShifts")}</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {Object.values(payrollData).reduce((sum, p) => sum + p.totalShifts, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-xl">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("employees.totalHours")}</p>
                      <p className="text-2xl font-bold text-slate-900">{totalHours.toFixed(1)}h</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-xl">
                      <Euro className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("employees.totalPayroll")}</p>
                      <p className="text-2xl font-bold text-slate-900">€{totalPayroll.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payroll Table */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5 text-green-500" />
                  {t("employees.payrollTitle")} {format(selectedPayrollMonth, 'MMMM yyyy', { locale: de })}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">{t("employees.employee")}</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">{t("employees.role")}</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-900">{t("employees.lunch")}</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-900">{t("employees.dinner")}</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-900">{t("employees.totalShiftsCol")}</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-900">{t("employees.hours")}</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-900">{t("employees.hourlyRateCol")}</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-900">{t("employees.totalSalary")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(payrollData)
                        .filter(p => p.totalShifts > 0)
                        .sort((a, b) => b.totalPay - a.totalPay)
                        .map((payroll) => (
                          <tr key={payroll.employee.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-4 px-4">
                              <div>
                                <p className="font-semibold text-slate-900">{payroll.employee.name}</p>
                                {!payroll.employee.active && (
                                  <Badge variant="outline" className="text-xs mt-1">{t("employees.inactive")}</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <Badge className={`${roleColors[payroll.employee.role]} border text-xs`}>
                                {roleLabels[payroll.employee.role] || payroll.employee.role}
                              </Badge>
                            </td>
                            <td className="text-center py-4 px-4">
                              <span className="inline-flex items-center justify-center px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                                {payroll.lunchShifts}
                              </span>
                            </td>
                            <td className="text-center py-4 px-4">
                              <span className="inline-flex items-center justify-center px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                                {payroll.dinnerShifts}
                              </span>
                            </td>
                            <td className="text-center py-4 px-4">
                              <span className="text-lg font-bold text-slate-900">{payroll.totalShifts}</span>
                            </td>
                            <td className="text-center py-4 px-4">
                              <span className="font-semibold text-slate-700">{payroll.totalHours.toFixed(1)}h</span>
                            </td>
                            <td className="text-right py-4 px-4">
                              <span className="text-slate-700">€{payroll.hourlyRate.toFixed(2)}/h</span>
                            </td>
                            <td className="text-right py-4 px-4">
                              <span className="text-xl font-bold text-green-600">
                                €{payroll.totalPay.toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      {Object.values(payrollData).filter(p => p.totalShifts > 0).length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center py-12 text-slate-500">
                            {t("employees.noShiftsInMonth")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {Object.values(payrollData).filter(p => p.totalShifts > 0).length > 0 && (
                      <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                        <tr>
                          <td colSpan={4} className="py-4 px-4 font-bold text-slate-900">{t("employees.total")}</td>
                          <td className="text-center py-4 px-4 font-bold text-slate-900">
                            {Object.values(payrollData).reduce((sum, p) => sum + p.totalShifts, 0)}
                          </td>
                          <td className="text-center py-4 px-4 font-bold text-slate-900">
                            {totalHours.toFixed(1)}h
                          </td>
                          <td className="text-right py-4 px-4"></td>
                          <td className="text-right py-4 px-4">
                            <span className="text-2xl font-bold text-green-600">
                              €{totalPayroll.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Breakdown per Employee */}
            {Object.values(payrollData).filter(p => p.totalShifts > 0).length > 0 && (
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">{t("employees.detailedBreakdown")}</h3>
                {Object.values(payrollData)
                  .filter(p => p.totalShifts > 0)
                  .sort((a, b) => b.totalPay - a.totalPay)
                  .map((payroll) => (
                    <Card key={payroll.employee.id} className="border-0 shadow-md">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                              {payroll.employee.name.charAt(0)}
                            </div>
                            <div>
                              <CardTitle>{payroll.employee.name}</CardTitle>
                              <p className="text-sm text-slate-500">{roleLabels[payroll.employee.role] || payroll.employee.role}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">€{payroll.totalPay.toFixed(2)}</p>
                            <p className="text-sm text-slate-500">{payroll.totalHours.toFixed(1)}h × €{payroll.hourlyRate.toFixed(2)}/h</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                            <p className="text-xs text-yellow-700 font-medium mb-1">{t("employees.lunchShifts")}</p>
                            <p className="text-2xl font-bold text-yellow-900">{payroll.lunchShifts}</p>
                          </div>
                          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                            <p className="text-xs text-indigo-700 font-medium mb-1">{t("employees.dinnerShifts")}</p>
                            <p className="text-2xl font-bold text-indigo-900">{payroll.dinnerShifts}</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <p className="text-xs text-purple-700 font-medium mb-1">{t("employees.totalHoursLabel")}</p>
                            <p className="text-2xl font-bold text-purple-900">{payroll.totalHours.toFixed(1)}h</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                            <p className="text-xs text-green-700 font-medium mb-1">{t("employees.hourlyRateCol")}</p>
                            <p className="text-2xl font-bold text-green-900">
                              €{payroll.hourlyRate.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-sm font-semibold text-slate-700 mb-2">{t("employees.plannedShifts")}</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                            {payroll.shifts.map((shift, idx) => (
                              <div key={idx} className="text-xs bg-slate-50 rounded p-2 border border-slate-200">
                                <p className="font-medium text-slate-900">{format(new Date(shift.date), 'dd.MM')}</p>
                                <Badge className={`${shift.shift_type === 'Mittag' ? 'bg-yellow-100 text-yellow-700' : 'bg-indigo-100 text-indigo-700'} mt-1 text-xs`}>
                                  {shift.shift_type}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </TabsContent>

          {/* User Accounts Tab */}
          <TabsContent value="user-accounts">
            <Dialog open={isUserDialogOpen} onOpenChange={(open) => {
              setIsUserDialogOpen(open);
              if (!open) setEditingUser(null);
            }}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Edit Employee</DialogTitle>
                </DialogHeader>
                {editingUser && (
                  <form onSubmit={handleUserSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name</Label>
                        <Input
                          value={userForm.first_name}
                          onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Last Name</Label>
                        <Input
                          value={userForm.last_name}
                          onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Hourly Rate (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={userForm.hourly_rate}
                        onChange={(e) => setUserForm({ ...userForm, hourly_rate: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="user-active"
                        checked={userForm.is_active}
                        onCheckedChange={(c) => setUserForm({ ...userForm, is_active: c })}
                      />
                      <Label htmlFor="user-active" className="cursor-pointer">Active account</Label>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Save</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {employeeUsers.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="text-center py-12">
                  <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No employee accounts found. Invite employees via the Organizations page.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {employeeUsers.map(user => {
                  const displayName = `${user.first_name} ${user.last_name}`.trim() || user.username;
                  const userTasks = allTasks.filter(t => t.assigned_user_id === user.id);
                  const todayTasks = userTasks.filter(t => t.due_date === today);
                  const activeTask = userTasks.find(t => t.started_at && !t.completed);
                  const isWorking = !!activeTask;

                  return (
                    <Card key={user.id} className={`border-0 shadow-md transition-all ${isWorking ? 'ring-2 ring-green-400' : ''}`}>
                      <CardContent className="p-5">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg ${isWorking ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{displayName}</p>
                              <p className="text-xs text-slate-500">@{user.username}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEditUser(user)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Working status */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${isWorking ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isWorking ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
                            {isWorking ? 'Working now' : 'Off duty'}
                          </span>
                          {user.hourly_rate > 0 && (
                            <span className="text-xs text-slate-500 ml-auto flex items-center gap-1">
                              <Euro className="w-3 h-3" />
                              {parseFloat(user.hourly_rate).toFixed(2)}/h
                            </span>
                          )}
                        </div>

                        {/* Active task */}
                        {activeTask && (
                          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3 text-xs">
                            <p className="text-green-800 font-medium truncate">{activeTask.title}</p>
                            <p className="text-green-600 mt-0.5">{activeTask.category}</p>
                          </div>
                        )}

                        {/* Today's tasks */}
                        {todayTasks.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1.5">Today's tasks ({todayTasks.length})</p>
                            <div className="space-y-1">
                              {todayTasks.slice(0, 3).map(task => (
                                <div key={task.id} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${task.completed ? 'bg-slate-50 text-slate-400' : 'bg-blue-50 text-blue-700'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${task.completed ? 'bg-slate-300' : task.started_at ? 'bg-green-500' : 'bg-blue-400'}`} />
                                  <span className={`truncate ${task.completed ? 'line-through' : ''}`}>{task.title}</span>
                                </div>
                              ))}
                              {todayTasks.length > 3 && (
                                <p className="text-xs text-slate-400 pl-2">+{todayTasks.length - 3} more</p>
                              )}
                            </div>
                          </div>
                        )}

                        {todayTasks.length === 0 && (
                          <p className="text-xs text-slate-400">No tasks assigned today</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
